// InputHandler.js - Handles all mouse and keyboard interactions
import { 
    getConnectionPointAt, 
    getConnectionAtPoint, 
    getResizeHandleAt 
} from './canvasRenderer.js';
import { calculateMarkdownHeight } from './markdownParser.js';

export class InputHandler {
    constructor(canvasState, uiManager, canvas, ctx) {
        this.canvasState = canvasState;
        this.uiManager = uiManager;
        this.canvas = canvas;
        this.ctx = ctx;
        
        // Interaction state
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragTarget = null;
        this.isConnecting = false;
        this.connectionStart = null;
        this.connectionStartPoint = null;
        this.hoveredNode = null;
        this.isResizing = false;
        this.resizeHandle = null;
        this.resizeTarget = null;
        this.isSelecting = false;
        this.selectionStart = { x: 0, y: 0 };
        this.selectionEnd = { x: 0, y: 0 };
        this.isPanning = false;
        this.spacePressed = false;
        this.lastMouseX = canvas.width / 2;
        this.lastMouseY = canvas.height / 2;
        
        // Node editing
        this.editingNode = null;
        this.editInput = null;
        this.editingMediaName = false;
        
        this.setupEventListeners();
    }
    
    setupEventListeners() {
        // Canvas mouse events
        this.canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
        this.canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
        this.canvas.addEventListener('mouseup', (e) => this.onMouseUp(e));
        this.canvas.addEventListener('wheel', (e) => this.onWheel(e), { passive: false });
        this.canvas.addEventListener('dblclick', (e) => this.onDoubleClick(e));
        this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());
        
        // Keyboard events
        document.addEventListener('keydown', (e) => this.onKeyDown(e));
        document.addEventListener('keyup', (e) => this.onKeyUp(e));
        document.addEventListener('paste', (e) => this.onPaste(e));
    }
    
    // Mouse event handlers
    onMouseDown(e) {
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        this.lastMouseX = mouseX;
        this.lastMouseY = mouseY;

        const canvasX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
        const canvasY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

        // Handle right mouse button or space+left click for panning
        if (e.button === 2 || (e.button === 0 && this.spacePressed)) {
            this.isPanning = true;
            this.isDragging = true;
            this.dragStartX = mouseX;
            this.dragStartY = mouseY;
            this.dragTarget = 'canvas';
            this.canvas.style.cursor = 'grabbing';
            return;
        }

        if (e.button !== 0) return;

        // Check for connection click
        const clickedConnection = getConnectionAtPoint(canvasX, canvasY, this.canvasState.connections, this.canvasState.nodes);
        if (clickedConnection) {
            this.canvasState.clearSelection();
            this.canvasState.selectedConnection = clickedConnection;
            return;
        }

        // Check for node click
        const clickedNode = this.getNodeAtPointWithExtendedArea(canvasX, canvasY);

        if (clickedNode) {
            // Check for connection point first (these should work anywhere on the node boundary)
            const connectionPoint = getConnectionPointAt(clickedNode, canvasX, canvasY);
            if (connectionPoint) {
                this.isConnecting = true;
                this.connectionStart = clickedNode;
                this.connectionStartPoint = connectionPoint;
                this.canvas.style.cursor = 'crosshair';
                return;
            }

            // For media reference nodes, only allow other interactions in header area
            if (clickedNode.isMediaReference && !this.isInMediaNodeHeader(clickedNode, canvasX, canvasY)) {
                // Click is in the media content area (iframe/image), don't interfere with other interactions
                return;
            }

            // Check for resize handle
            let resizeHandle = null;
            if (clickedNode.isSelected) {
                resizeHandle = getResizeHandleAt(clickedNode, canvasX, canvasY);
            }

            if (!resizeHandle) {
                resizeHandle = this.getBorderResizeHandle(clickedNode, canvasX, canvasY);
            }

            if (resizeHandle) {
                if (this.canvasState.selectedNodes.length > 1) {
                    this.canvasState.selectNodes(clickedNode);
                } else if (!clickedNode.isSelected) {
                    this.canvasState.selectNodes(clickedNode);
                }
                this.isResizing = true;
                this.resizeHandle = resizeHandle;
                this.resizeTarget = clickedNode;
                this.setCursorForResize(resizeHandle);
                this.dragStartX = mouseX;
                this.dragStartY = mouseY;
                return;
            } else {
                // Handle multi-selection
                if (e.ctrlKey || e.metaKey) {
                    if (clickedNode.isSelected) {
                        this.canvasState.removeFromSelection(clickedNode);
                    } else {
                        this.canvasState.addToSelection(clickedNode);
                    }
                } else {
                    if (!clickedNode.isSelected) {
                        this.canvasState.selectNodes(clickedNode);
                    }
                }

                this.dragTarget = this.canvasState.selectedNodes.length > 1 ? 'selectedNodes' : clickedNode;
            }
        } else {
            // Clicked on empty space
            if (!(e.ctrlKey || e.metaKey)) {
                this.canvasState.clearSelection();
            } else {
                this.canvasState.selectedConnection = null;
            }

            if (!this.spacePressed) {
                this.isSelecting = true;
                this.selectionStart = { x: canvasX, y: canvasY };
                this.selectionEnd = { x: canvasX, y: canvasY };
            }
        }

        if (!this.isConnecting && !this.isResizing && !this.isSelecting) {
            this.isDragging = true;
            this.dragStartX = mouseX;
            this.dragStartY = mouseY;

            if (this.dragTarget === 'canvas') {
                this.canvas.style.cursor = 'grabbing';
            }
        } else if (this.isSelecting) {
            this.canvas.style.cursor = 'crosshair';
        } else if (!this.isConnecting && !this.isResizing) {
            this.isDragging = true;
            this.dragStartX = mouseX;
            this.dragStartY = mouseY;
        }

        this.uiManager.updateGenerateIdeasTooltip();
        this.uiManager.updateSelectionStatus();
    }
    
    onMouseMove(e) {
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        this.lastMouseX = mouseX;
        this.lastMouseY = mouseY;
        
        const canvasX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
        const canvasY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;
        
        // Update cursor
        if (!this.isDragging && !this.isConnecting && !this.isResizing) {
            if (this.spacePressed) {
                this.canvas.style.cursor = 'grab';
            } else {
                const hoveredNode = this.getNodeAtPointWithExtendedArea(canvasX, canvasY);

                if (this.hoveredNode !== hoveredNode) {
                    this.hoveredNode = hoveredNode;
                }

                if (hoveredNode) {
                    const connectionPoint = getConnectionPointAt(hoveredNode, canvasX, canvasY);
                    if (connectionPoint) {
                        this.canvas.style.cursor = 'crosshair';
                    } else {
                        let resizeHandle = null;
                        if (hoveredNode.isSelected) {
                            resizeHandle = getResizeHandleAt(hoveredNode, canvasX, canvasY);
                        }

                        if (!resizeHandle) {
                            resizeHandle = this.getBorderResizeHandle(hoveredNode, canvasX, canvasY);
                        }

                        if (resizeHandle) {
                            this.setCursorForResize(resizeHandle);
                        } else {
                            this.canvas.style.cursor = 'grab';
                        }
                    }
                } else {
                    this.canvas.style.cursor = 'default';
                }
            }
        }
        
        if (this.isSelecting) {
            this.selectionEnd = { x: canvasX, y: canvasY };
            return;
        }

        if (this.isConnecting) {
            return;
        }

        if (this.isResizing) {
            const deltaX = mouseX - this.dragStartX;
            const deltaY = mouseY - this.dragStartY;

            this.resizeTarget.manuallyResized = true;

            const minWidth = 60;
            const minHeight = 40;
            const scaledDeltaX = deltaX / this.canvasState.scale;
            const scaledDeltaY = deltaY / this.canvasState.scale;

            switch(this.resizeHandle) {
                case 'top':
                    const newHeightTop = this.resizeTarget.height - scaledDeltaY;
                    if (newHeightTop >= minHeight) {
                        this.resizeTarget.y += scaledDeltaY;
                        this.resizeTarget.height = newHeightTop;
                    }
                    break;
                case 'bottom':
                    this.resizeTarget.height = Math.max(minHeight, this.resizeTarget.height + scaledDeltaY);
                    break;
                case 'left':
                    const newWidthLeft = this.resizeTarget.width - scaledDeltaX;
                    if (newWidthLeft >= minWidth) {
                        this.resizeTarget.x += scaledDeltaX;
                        this.resizeTarget.width = newWidthLeft;
                    }
                    break;
                case 'right':
                    this.resizeTarget.width = Math.max(minWidth, this.resizeTarget.width + scaledDeltaX);
                    break;
                case 'top-left':
                    const newWidthTL = this.resizeTarget.width - scaledDeltaX;
                    const newHeightTL = this.resizeTarget.height - scaledDeltaY;
                    if (newWidthTL >= minWidth && newHeightTL >= minHeight) {
                        this.resizeTarget.x += scaledDeltaX;
                        this.resizeTarget.y += scaledDeltaY;
                        this.resizeTarget.width = newWidthTL;
                        this.resizeTarget.height = newHeightTL;
                    }
                    break;
                case 'top-right':
                    const newHeightTR = this.resizeTarget.height - scaledDeltaY;
                    if (newHeightTR >= minHeight) {
                        this.resizeTarget.y += scaledDeltaY;
                        this.resizeTarget.height = newHeightTR;
                    }
                    this.resizeTarget.width = Math.max(minWidth, this.resizeTarget.width + scaledDeltaX);
                    break;
                case 'bottom-left':
                    const newWidthBL = this.resizeTarget.width - scaledDeltaX;
                    if (newWidthBL >= minWidth) {
                        this.resizeTarget.x += scaledDeltaX;
                        this.resizeTarget.width = newWidthBL;
                    }
                    this.resizeTarget.height = Math.max(minHeight, this.resizeTarget.height + scaledDeltaY);
                    break;
                case 'bottom-right':
                    this.resizeTarget.width = Math.max(minWidth, this.resizeTarget.width + scaledDeltaX);
                    this.resizeTarget.height = Math.max(minHeight, this.resizeTarget.height + scaledDeltaY);
                    break;
            }

            this.dragStartX = mouseX;
            this.dragStartY = mouseY;
            return;
        }

        if (!this.isDragging) return;

        const deltaX = mouseX - this.dragStartX;
        const deltaY = mouseY - this.dragStartY;

        if (this.dragTarget === 'canvas') {
            this.canvasState.offsetX += deltaX;
            this.canvasState.offsetY += deltaY;
        } else if (this.dragTarget === 'selectedNodes') {
            const scaledDeltaX = deltaX / this.canvasState.scale;
            const scaledDeltaY = deltaY / this.canvasState.scale;
            this.canvasState.selectedNodes.forEach(node => {
                node.x += scaledDeltaX;
                node.y += scaledDeltaY;
                this.canvasState.removeNodeFromGrid(node);
                this.canvasState.addNodeToGrid(node);
            });
        } else if (this.dragTarget && typeof this.dragTarget === 'object') {
            this.canvasState.removeNodeFromGrid(this.dragTarget);
            this.dragTarget.x += deltaX / this.canvasState.scale;
            this.dragTarget.y += deltaY / this.canvasState.scale;
            this.canvasState.addNodeToGrid(this.dragTarget);
        }

        this.dragStartX = mouseX;
        this.dragStartY = mouseY;
    }
    
    onMouseUp(e) {
        if (this.isSelecting) {
            this.isSelecting = false;

            const rect = {
                x: Math.min(this.selectionStart.x, this.selectionEnd.x),
                y: Math.min(this.selectionStart.y, this.selectionEnd.y),
                width: Math.abs(this.selectionEnd.x - this.selectionStart.x),
                height: Math.abs(this.selectionEnd.y - this.selectionStart.y)
            };

            const nodesInSelection = this.canvasState.nodes.filter(node => {
                if (!this.canvasState.isNodeInActiveThread(node)) {
                    return false;
                }
                return node.x < rect.x + rect.width &&
                       node.x + node.width > rect.x &&
                       node.y < rect.y + rect.height &&
                       node.y + node.height > rect.y;
            });

            if (e.ctrlKey || e.metaKey) {
                nodesInSelection.forEach(node => this.canvasState.addToSelection(node));
            } else {
                this.canvasState.selectNodes(nodesInSelection);
            }

            this.canvas.style.cursor = 'grab';
            this.uiManager.updateGenerateIdeasTooltip();
            this.uiManager.updateSelectionStatus();
            return;
        }

        if (this.isConnecting) {
            const rect = this.canvas.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const canvasX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
            const canvasY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

            const targetNode = this.getNodeAtPointWithExtendedArea(canvasX, canvasY);

            if (targetNode && targetNode !== this.connectionStart) {
                this.canvasState.createConnection(this.connectionStart, targetNode);
            } else if (!targetNode) {
                const newNode = this.canvasState.createNode('New Node', canvasX - 200, canvasY - 30);
                this.canvasState.createConnection(this.connectionStart, newNode);
                this.canvasState.selectNodes(newNode);
                setTimeout(() => {
                    this.startEditingNode(newNode);
                }, 50);
            }

            this.isConnecting = false;
            this.connectionStart = null;
            this.connectionStartPoint = null;
            this.canvas.style.cursor = 'grab';
            this.canvasState.saveToLocalStorage();
            this.uiManager.updateUndoRedoButtons();
            return;
        }

        if (this.isResizing) {
            this.isResizing = false;
            this.resizeHandle = null;
            this.resizeTarget = null;
            this.canvas.style.cursor = 'grab';
            this.canvasState.saveToLocalStorage();
            this.uiManager.updateUndoRedoButtons();
            return;
        }

        this.isDragging = false;
        this.isPanning = false;
        this.dragTarget = null;
        this.canvas.style.cursor = this.spacePressed ? 'grab' : 'default';

        if (this.dragTarget !== 'canvas') {
            this.canvasState.saveToLocalStorage();
        }

        if ((this.isDragging && this.dragTarget !== 'canvas' && !this.isPanning) || this.isResizing) {
            this.canvasState.saveState();
        }
        
        if (this.isConnecting && this.hoveredNode && this.connectionStart.id !== this.hoveredNode.id) {
            this.canvasState.createConnection(this.connectionStart, this.hoveredNode);
        }

        this.uiManager.updateUndoRedoButtons();
        this.editingNode = null;
    }
    
    onWheel(e) {
        e.preventDefault();

        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const canvasX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
        const canvasY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

        // Check for node scrolling
        const hoveredNode = this.canvasState.getNodeAtPoint(canvasX, canvasY);
        if (hoveredNode && hoveredNode.isSelected && hoveredNode.contentHeight > hoveredNode.height) {
            const scrollSensitivity = 20;
            const maxScrollY = hoveredNode.contentHeight - hoveredNode.height;

            if (hoveredNode.scrollY === undefined) {
                hoveredNode.scrollY = 0;
            }

            hoveredNode.scrollY = Math.max(0, Math.min(maxScrollY, hoveredNode.scrollY + e.deltaY * scrollSensitivity / 100));
            return;
        }

        // Detect trackpad vs mouse wheel
        const isTrackpad = Math.abs(e.deltaY) < 50 || Math.abs(e.deltaX) > 0;

        // Handle trackpad pinch-to-zoom
        if (e.ctrlKey && isTrackpad) {
            const zoomFactor = e.deltaY > 0 ? 0.95 : 1.05;
            const newScale = Math.max(0.1, Math.min(3.0, this.canvasState.scale * zoomFactor));

            const beforeZoomX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
            const beforeZoomY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

            this.canvasState.scale = newScale;

            const afterZoomX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
            const afterZoomY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

            this.canvasState.offsetX += (afterZoomX - beforeZoomX) * this.canvasState.scale;
            this.canvasState.offsetY += (afterZoomY - beforeZoomY) * this.canvasState.scale;
            return;
        }

        // Handle trackpad two-finger scrolling for panning
        if (isTrackpad && !e.ctrlKey && !e.metaKey) {
            const panSensitivity = 1.0;
            this.canvasState.offsetX -= e.deltaX * panSensitivity;
            this.canvasState.offsetY -= e.deltaY * panSensitivity;
            return;
        }

        // Handle zoom
        const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.1, Math.min(3.0, this.canvasState.scale * zoomFactor));

        const beforeZoomX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
        const beforeZoomY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

        this.canvasState.scale = newScale;

        const afterZoomX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
        const afterZoomY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;

        this.canvasState.offsetX += (afterZoomX - beforeZoomX) * this.canvasState.scale;
        this.canvasState.offsetY += (afterZoomY - beforeZoomY) * this.canvasState.scale;
    }
    
    onDoubleClick(e) {
        const rect = this.canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        const canvasX = (mouseX - this.canvasState.offsetX) / this.canvasState.scale;
        const canvasY = (mouseY - this.canvasState.offsetY) / this.canvasState.scale;
        
        const clickedNode = this.getNodeAtPointWithExtendedArea(canvasX, canvasY);
        
        if (clickedNode) {
            // Check if it's a media reference node and we're clicking in the header
            if (clickedNode.isMediaReference && this.isInMediaNodeHeader(clickedNode, canvasX, canvasY)) {
                this.startEditingMediaNodeName(clickedNode);
            } else if (!clickedNode.isMediaReference) {
                // Regular text node editing
                this.startEditingNode(clickedNode);
            }
        } else {
            const newNode = this.canvasState.createNode('New Node', canvasX - 100, canvasY - 30);
            setTimeout(() => {
                this.startEditingNode(newNode);
            }, 50);
        }
    }
    
    // Keyboard event handlers
    onKeyDown(e) {
        // Only intercept shortcuts when the canvas view is active. In chat mode we
        // should let the browser handle copy/paste/select shortcuts so users can
        // interact with text normally.
        if (this.uiManager && this.uiManager.currentViewMode !== 'canvas') {
            return;
        }

        if (e.key === ' ' && !this.spacePressed) {
            if (this.editingNode || document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
                return;
            }
            e.preventDefault();
            this.spacePressed = true;
            if (!this.isDragging) {
                this.canvas.style.cursor = 'grab';
            }
            return;
        }

        if (this.editingNode || document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
            return;
        }

        switch(e.key) {
            case 'Delete':
            case 'Backspace':
                if (this.canvasState.selectedNodes.length > 0) {
                    this.canvasState.deleteSelectedNodes();
                } else if (this.canvasState.selectedConnection) {
                    this.deleteSelectedConnection();
                }
                this.uiManager.updateUndoRedoButtons();
                break;
            case 'a':
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    const threadNodes = this.canvasState.nodes.filter(node => this.canvasState.isNodeInActiveThread(node));
                    this.canvasState.selectNodes(threadNodes);
                }
                break;
            case 'c':
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    this.canvasState.copySelectedNodes();
                }
                break;
            case 'v':
                if (e.ctrlKey || e.metaKey) {
                    // Let the default browser paste action continue so the 'paste' event fires.
                    // First, attempt to paste internal canvas nodes (duplicates) if any exist.
                    this.canvasState.pasteNodes();
                    this.uiManager.updateUndoRedoButtons();
                }
                break;
            case 'z':
                if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
                    e.preventDefault();
                    this.canvasState.redo();
                    this.uiManager.updateUndoRedoButtons();
                } else if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    this.canvasState.undo();
                    this.uiManager.updateUndoRedoButtons();
                }
                break;
            case 'y':
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    this.canvasState.redo();
                    this.uiManager.updateUndoRedoButtons();
                }
                break;
        }

        this.uiManager.updateGenerateIdeasTooltip();
        this.uiManager.updateSelectionStatus();
    }

    onKeyUp(e) {
        if (this.uiManager && this.uiManager.currentViewMode !== 'canvas') {
            return;
        }

        if (e.key === ' ') {
            this.spacePressed = false;
            if (!this.isDragging) {
                this.canvas.style.cursor = 'default';
            }
        }
    }
    
    // Helper methods
    getNodeAtPointWithExtendedArea(x, y) {
        const hoverExtension = 24;

        let foundNode = this.canvasState.getNodeAtPoint(x, y);
        if (foundNode) {
            return foundNode;
        }

        for (let i = this.canvasState.nodes.length - 1; i >= 0; i--) {
            const node = this.canvasState.nodes[i];
            if (!this.canvasState.isNodeInActiveThread(node)) {
                continue;
            }

            if (x >= node.x - hoverExtension && x <= node.x + node.width + hoverExtension &&
                y >= node.y - hoverExtension && y <= node.y + node.height + hoverExtension) {
                return node;
            }
        }

        return null;
    }

    // Check if click is in the draggable header area of a media reference node
    isInMediaNodeHeader(node, x, y) {
        if (!node.isMediaReference || !node._headerHeight) return false;
        
        return x >= node.x && x <= node.x + node.width &&
               y >= node.y && y <= node.y + node._headerHeight;
    }

    getBorderResizeHandle(node, x, y) {
        const connectionPoint = getConnectionPointAt(node, x, y);
        if (connectionPoint) {
            return null;
        }

        const borderTolerance = 24;
        const cornerSize = 40;

        const nodeLeft = node.x;
        const nodeRight = node.x + node.width;
        const nodeTop = node.y;
        const nodeBottom = node.y + node.height;

        const nearLeft = Math.abs(x - nodeLeft) <= borderTolerance;
        const nearRight = Math.abs(x - nodeRight) <= borderTolerance;
        const nearTop = Math.abs(y - nodeTop) <= borderTolerance;
        const nearBottom = Math.abs(y - nodeBottom) <= borderTolerance;

        const withinHorizontalBounds = x >= nodeLeft - borderTolerance && x <= nodeRight + borderTolerance;
        const withinVerticalBounds = y >= nodeTop - borderTolerance && y <= nodeBottom + borderTolerance;

        // Corner resize handles
        if (nearTop && nearLeft &&
            x >= nodeLeft - borderTolerance && x <= nodeLeft + cornerSize &&
            y >= nodeTop - borderTolerance && y <= nodeTop + cornerSize) {
            return 'top-left';
        }

        if (nearTop && nearRight &&
            x >= nodeRight - cornerSize && x <= nodeRight + borderTolerance &&
            y >= nodeTop - borderTolerance && y <= nodeTop + cornerSize) {
            return 'top-right';
        }

        if (nearBottom && nearLeft &&
            x >= nodeLeft - borderTolerance && x <= nodeLeft + cornerSize &&
            y >= nodeBottom - cornerSize && y <= nodeBottom + borderTolerance) {
            return 'bottom-left';
        }

        if (nearBottom && nearRight &&
            x >= nodeRight - cornerSize && x <= nodeRight + borderTolerance &&
            y >= nodeBottom - cornerSize && y <= nodeBottom + borderTolerance) {
            return 'bottom-right';
        }

        // Edge resize handles
        if (nearTop && withinHorizontalBounds &&
            x > nodeLeft + cornerSize && x < nodeRight - cornerSize) {
            return 'top';
        }

        if (nearBottom && withinHorizontalBounds &&
            x > nodeLeft + cornerSize && x < nodeRight - cornerSize) {
            return 'bottom';
        }

        if (nearLeft && withinVerticalBounds &&
            y > nodeTop + cornerSize && y < nodeBottom - cornerSize) {
            return 'left';
        }

        if (nearRight && withinVerticalBounds &&
            y > nodeTop + cornerSize && y < nodeBottom - cornerSize) {
            return 'right';
        }

        return null;
    }

    setCursorForResize(handle) {
        const nsResizeCursor = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cg%3E%3Cpath d='M8 2l-2 2h1v8h-1l2 2 2-2h-1V4h1l-2-2z' stroke='white' stroke-width='3' fill='white'/%3E%3C/g%3E%3C/svg%3E"), ns-resize`;
        const ewResizeCursor = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cg%3E%3Cpath d='M2 8l2-2v1h8v-1l2 2-2 2v-1H4v1l-2-2z' stroke='white' stroke-width='3' fill='white'/%3E%3C/g%3E%3C/svg%3E"), ew-resize`;
        const nwseResizeCursor = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cg%3E%3Cpath d='M3 3l2 0v1l6 6h1v2l-2-2h-1L3 4v-1z' stroke='white' stroke-width='3' fill='black'/%3E%3Cpath d='M13 13l-2 0v-1l-6-6h-1v-2l2 2h1l6 6v1z' stroke='white' stroke-width='2' fill='black'/%3E%3C/g%3E%3C/svg%3E"), nw-resize`;
        const neswResizeCursor = `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'%3E%3Cg%3E%3Cpath d='M13 3l-2 0v1l-6 6h-1v2l2-2h1l6-6v-1z' stroke='white' stroke-width='3' fill='black'/%3E%3Cpath d='M3 13l2 0v-1l6-6h1v-2l-2 2h-1L3 12v1z' stroke='white' stroke-width='2' fill='black'/%3E%3C/g%3E%3C/svg%3E"), ne-resize`;

        switch(handle) {
            case 'top':
            case 'bottom':
                this.canvas.style.cursor = nsResizeCursor;
                break;
            case 'left':
            case 'right':
                this.canvas.style.cursor = ewResizeCursor;
                break;
            case 'top-left':
            case 'bottom-right':
                this.canvas.style.cursor = nwseResizeCursor;
                break;
            case 'top-right':
            case 'bottom-left':
                this.canvas.style.cursor = neswResizeCursor;
                break;
            default:
                this.canvas.style.cursor = 'default';
        }
    }

    deleteSelectedConnection() {
        if (this.canvasState.selectedConnection) {
            const connId = this.canvasState.selectedConnection.id;
            this.canvasState.connections = this.canvasState.connections.filter(c => c.id !== connId);
            this.canvasState.selectedConnection = null;
            this.canvasState.saveState();
            this.uiManager.updateUndoRedoButtons();
        }
    }

    // Node editing
    startEditingNode(node) {
        this.editingNode = node;
        
        this.editInput = document.createElement('textarea');
        this.editInput.value = node.text;
        this.editInput.className = 'node-input';
        
        const rect = this.canvas.getBoundingClientRect();
        const x = rect.left + (node.x * this.canvasState.scale) + this.canvasState.offsetX;
        const y = rect.top + (node.y * this.canvasState.scale) + this.canvasState.offsetY;

        this.editInput.style.left = x + 'px';
        this.editInput.style.top = y + 'px';
        this.editInput.style.width = (node.width * this.canvasState.scale) + 'px';
        this.editInput.style.height = (node.height * this.canvasState.scale) + 'px';

        const nodeFontSize = node.fontSize || this.canvasState.globalFontSize;
        this.editInput.style.fontSize = (nodeFontSize * this.canvasState.scale) + 'px';
        
        document.body.appendChild(this.editInput);
        this.editInput.focus();
        this.editInput.select();
        
        this.editInput.addEventListener('blur', () => this.finishEditingNode());
        this.editInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.finishEditingNode();
            }
        });
    }
    
    finishEditingNode() {
        if (this.editingNode && this.editInput) {
            if (this.editingMediaName) {
                this.finishEditingMediaNodeName();
                return;
            }
            
            const newText = this.editInput.value;
            if (this.editingNode.text !== newText) {
                this.editingNode.text = newText;
                this.editingNode.height = calculateMarkdownHeight(this.ctx, newText, this.editingNode.width, this.editingNode.fontSize);
                this.canvasState.saveState();
                this.uiManager.updateUndoRedoButtons();
            }

            document.body.removeChild(this.editInput);
            this.editInput = null;
            this.editingNode = null;
        }
    }

    finishEditingMediaNodeName() {
        if (this.editingNode && this.editInput && this.editingMediaName) {
            const newName = this.editInput.value.trim();
            if (this.editingNode.name !== newName) {
                this.canvasState.saveState();
                this.editingNode.name = newName;
                this.canvasState.saveToLocalStorage();
                this.uiManager.updateUndoRedoButtons();
            }

            document.body.removeChild(this.editInput);
            this.editInput = null;
            this.editingNode = null;
            this.editingMediaName = false;
        }
    }

    cancelEditingMediaNodeName() {
        if (this.editInput) {
            document.body.removeChild(this.editInput);
            this.editInput = null;
            this.editingNode = null;
            this.editingMediaName = false;
        }
    }

    startEditingMediaNodeName(node) {
        this.editingNode = node;
        this.editingMediaName = true;
        
        this.editInput = document.createElement('input');
        this.editInput.type = 'text';
        this.editInput.value = node.name || '';
        this.editInput.className = 'media-name-input';
        this.editInput.placeholder = 'Enter image name...';
        
        const rect = this.canvas.getBoundingClientRect();
        const x = rect.left + (node.x * this.canvasState.scale) + this.canvasState.offsetX;
        const y = rect.top + (node.y * this.canvasState.scale) + this.canvasState.offsetY;

        this.editInput.style.left = (x + 8) + 'px';
        this.editInput.style.top = (y + 5) + 'px';
        this.editInput.style.width = ((node.width - 16) * this.canvasState.scale) + 'px';
        this.editInput.style.height = '20px';
        this.editInput.style.fontSize = '12px';
        this.editInput.style.fontWeight = 'bold';
        this.editInput.style.background = '#374151';
        this.editInput.style.color = '#e2e8f0';
        this.editInput.style.border = '1px solid #4299e1';
        this.editInput.style.borderRadius = '3px';
        this.editInput.style.padding = '2px 4px';
        
        document.body.appendChild(this.editInput);
        this.editInput.focus();
        this.editInput.select();
        
        this.editInput.addEventListener('blur', () => this.finishEditingMediaNodeName());
        this.editInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this.finishEditingMediaNodeName();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                this.cancelEditingMediaNodeName();
            }
        });
    }

    // Canvas controls
    zoom(factor) {
        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        
        const beforeZoomX = (centerX - this.canvasState.offsetX) / this.canvasState.scale;
        const beforeZoomY = (centerY - this.canvasState.offsetY) / this.canvasState.scale;
        
        this.canvasState.scale = Math.max(0.1, Math.min(3.0, this.canvasState.scale * factor));
        
        const afterZoomX = (centerX - this.canvasState.offsetX) / this.canvasState.scale;
        const afterZoomY = (centerY - this.canvasState.offsetY) / this.canvasState.scale;
        
        this.canvasState.offsetX += (afterZoomX - beforeZoomX) * this.canvasState.scale;
        this.canvasState.offsetY += (afterZoomY - beforeZoomY) * this.canvasState.scale;
    }

    resetView() {
        this.canvasState.resetView();
    }

    increaseFontSize() {
        const nodesToChange = this.canvasState.selectedNodes.length > 0
            ? this.canvasState.selectedNodes
            : this.canvasState.nodes.filter(node => this.canvasState.isNodeInActiveThread(node));
        nodesToChange.forEach(node => {
            node.fontSize = (node.fontSize || this.canvasState.globalFontSize) + 2;
            if (!node.manuallyResized) {
                node.height = calculateMarkdownHeight(this.ctx, node.text, node.width, node.fontSize);
            }
        });

        if (this.canvasState.selectedNodes.length === 0) {
            this.canvasState.globalFontSize += 2;
        }

        this.canvasState.saveState();
        this.uiManager.updateUndoRedoButtons();
    }

    decreaseFontSize() {
        const nodesToChange = this.canvasState.selectedNodes.length > 0
            ? this.canvasState.selectedNodes
            : this.canvasState.nodes.filter(node => this.canvasState.isNodeInActiveThread(node));
        nodesToChange.forEach(node => {
            node.fontSize = Math.max(8, (node.fontSize || this.canvasState.globalFontSize) - 2);
            if (!node.manuallyResized) {
                node.height = calculateMarkdownHeight(this.ctx, node.text, node.width, node.fontSize);
            }
        });

        if (this.canvasState.selectedNodes.length === 0) {
            this.canvasState.globalFontSize = Math.max(8, this.canvasState.globalFontSize - 2);
        }

        this.canvasState.saveState();
        this.uiManager.updateUndoRedoButtons();
    }

    // Handle clipboard paste for images / videos / URLs
    onPaste(e) {
        if (this.editingNode) {
            return;
        }

        // Ignore if currently typing inside an input or textarea
        const activeTag = document.activeElement.tagName;
        if (activeTag === 'INPUT' || activeTag === 'TEXTAREA') return;

        const clipboard = e.clipboardData;
        if (!clipboard) return;

        const items = clipboard.items || [];
        let handled = false;

        // Helper to convert current screen coords to canvas coords
        const getPasteCoords = () => {
            const canvasX = (this.lastMouseX - this.canvasState.offsetX) / this.canvasState.scale;
            const canvasY = (this.lastMouseY - this.canvasState.offsetY) / this.canvasState.scale;
            return { x: canvasX, y: canvasY };
        };

        // 1. First check for URLs in text (higher priority than files for web content)
        const textData = clipboard.getData('text');
        if (textData && !handled) {
            const trimmedUrl = textData.trim();
            
            // Check for YouTube URLs
            if (this.isYouTubeUrl(trimmedUrl)) {
                const { x, y } = getPasteCoords();
                this.createMediaNode(trimmedUrl, 'youtube', x, y);
                handled = true;
            }
            // Check for other video platform URLs
            else if (this.isVideoUrl(trimmedUrl)) {
                const { x, y } = getPasteCoords();
                this.createMediaNode(trimmedUrl, 'video', x, y);
                handled = true;
            }
            // Check for direct media file URLs
            else if (this.isDirectMediaUrl(trimmedUrl)) {
                const { x, y } = getPasteCoords();
                const isImage = /(png|jpe?g|gif|webp|bmp|svg)$/i.test(trimmedUrl.toLowerCase());
                this.createMediaNode(trimmedUrl, isImage ? 'image' : 'video', x, y);
                handled = true;
            }
        }

        // 2A. Handle pasted files via items (Chrome/Edge/Firefox) - only if no URL was handled
        if (!handled) {
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (!file) continue;
                    if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                        const url = URL.createObjectURL(file);
                        const { x, y } = getPasteCoords();
                        this.createMediaNode(url, file.type.startsWith('image/') ? 'image' : 'video', x, y, file);
                        handled = true;
                    }
                }
            }
        }

        // 2B. Fallback for clipboard.files (Safari) - only if no URL or file was handled
        if (!handled && clipboard.files && clipboard.files.length > 0) {
            Array.from(clipboard.files).forEach(file => {
                if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
                    const url = URL.createObjectURL(file);
                    const { x, y } = getPasteCoords();
                    this.createMediaNode(url, file.type.startsWith('image/') ? 'image' : 'video', x, y, file);
                    handled = true;
                }
            });
        }

        if (handled) {
            e.preventDefault();
        }
    }

    // Helper methods for URL detection
    isYouTubeUrl(url) {
        const youtubeRegex = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)/i;
        return youtubeRegex.test(url);
    }

    isVideoUrl(url) {
        // Common video platforms
        const videoSites = [
            'vimeo.com',
            'dailymotion.com',
            'twitch.tv',
            'tiktok.com',
            'instagram.com',
            'twitter.com',
            'x.com'
        ];
        
        try {
            const urlObj = new URL(url);
            return videoSites.some(site => urlObj.hostname.includes(site));
        } catch {
            return false;
        }
    }

    isDirectMediaUrl(url) {
        // Direct file URLs
        const mediaRegex = /\.(png|jpe?g|gif|webp|bmp|svg|mp4|webm|mov|ogg|m4v|avi|mkv)(\?.*)?$/i;
        return mediaRegex.test(url);
    }

    // Utility to create a minimal media reference node
    createMediaNode(url, mediaType, x, y, file = null) {
        // Store previously selected nodes (to potentially auto-connect)
        const previousSelection = [...this.canvasState.selectedNodes];

        const node = this.canvasState.createNode('', x, y, null);
        node.width = 320;
        node.height = mediaType === 'image' ? 240 : 270; // Increased video height to account for header
        node.isMediaReference = true;
        node.manuallyResized = true; // prevent auto resize
        node._headerHeight = 50; // Set header height for consistent UI across all media nodes

        if (mediaType === 'image') {
            node.generatedImageUrl = url;
        } else if (mediaType === 'youtube') {
            node.generatedVideoUrl = url;
            node.isYouTubeVideo = true;
            // Set custom dimensions for YouTube videos (accounting for header)
            node.width = 480;
            node.height = 320; // 270 (16:9 video) + 50 (header) = 320 total
        } else {
            node.generatedVideoUrl = url;
        }

        // Auto-connect to previously selected node if exactly one was selected
        if (previousSelection.length === 1) {
            this.canvasState.createConnection(previousSelection[0], node);
        }

        // Update spatial grid with new size
        this.canvasState.removeNodeFromGrid(node);
        this.canvasState.addNodeToGrid(node);

        // Select the newly created node so user can immediately move it
        this.canvasState.clearSelection();
        this.canvasState.selectNodes(node);

        // Save state for undo history (includes possible auto-connection)
        this.canvasState.saveState();
        
        // Save to localStorage to persist across page reloads
        this.canvasState.saveToLocalStorage();

        // If we pasted a local file image, upload it automatically so it gets a public URL
        if (mediaType === 'image' && file && url.startsWith('blob:')) {
            this.uiManager.showNotification('Uploading image…', 'info', 3000);
            this.uploadFileToLitterbox(file)
                .then(remoteUrl => {
                    node.generatedImageUrl = remoteUrl;
                    this.canvasState.saveToLocalStorage();
                    this.uiManager.showNotification('Image uploaded successfully!', 'success');
                })
                .catch(err => {
                    console.error('Image upload failed:', err);
                    this.uiManager.showNotification('Image upload failed: ' + err.message, 'error');
                });
        }

        if (this.uiManager && typeof this.uiManager.updateSelectionStatus === 'function') {
            this.uiManager.updateSelectionStatus();
        }
    }

    // Helper to upload an image file to Litterbox (72-hour temporary hosting)
    uploadFileToLitterbox(file) {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append('reqtype', 'fileupload');
            formData.append('time', '72h');
            formData.append('fileToUpload', file);

            const xhr = new XMLHttpRequest();
            xhr.onload = function () {
                if (xhr.status === 200) {
                    const response = xhr.responseText.trim();
                    if (response.startsWith('https://')) {
                        resolve(response);
                    } else {
                        reject(new Error('Invalid response from upload service: ' + response));
                    }
                } else {
                    reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                }
            };
            xhr.onerror = function () {
                reject(new Error('Network error during upload'));
            };
            xhr.ontimeout = function () {
                reject(new Error('Upload timed out'));
            };
            xhr.timeout = 60000; // 1 min
            xhr.open('POST', 'https://litterbox.catbox.moe/resources/internals/api.php');
            xhr.send(formData);
        });
    }
} 